/*
 * Assembly Add-On — Online Store 2.0 service add-on for the Empire theme
 * --------------------------------------------------------------------------
 * Two responsibilities, both driven entirely by the product metafield
 * custom.assembly_product (no hardcoded SKUs / prices anywhere):
 *
 *   1) Product page: when the shopper ticks "Professional Assembly", intercept
 *      the theme's Add-to-Cart submit and add BOTH the main product and the
 *      referenced hidden assembly product in a SINGLE /cart/add.js request, as
 *      two separate line items. When unticked, the theme adds the product as
 *      normal (we do nothing).
 *
 *   2) Cart validation (runs on every page): if an assembly line item is in the
 *      cart without its parent product, remove it automatically.
 *
 * Loaded globally from layout/theme.liquid so the cart validation runs on the
 * cart page, the header flyout, and everywhere a line item can be removed.
 * The markup + CSS are injected only on product pages by sections/assembly-addon.liquid.
 */
(function () {
  'use strict';

  var ROUTES = (window.Theme && window.Theme.routes) || {};
  var ADD_URL = (ROUTES.cart_add_url || '/cart/add');
  var CART_URL = (ROUTES.cart_url || '/cart');
  var CHANGE_URL = (ROUTES.cart_change_url || '/cart/change');
  var UPDATE_URL = CART_URL + '/update';

  var ASSEMBLY_FLAG = '_is_assembly';
  var ASSEMBLY_PARENT = '_assembly_for_product_id';

  function jsonHeaders() {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  function getCart() {
    return fetch(CART_URL + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function notifyCart(cart) {
    // Mirror the theme's own post-add behavior so the header count updates.
    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
  }

  /* ------------------------------------------------------------------ *
   * 1) Product page — single-request add of product + assembly
   * ------------------------------------------------------------------ */

  function findProductForm() {
    var btn = document.querySelector('[data-product-form] [data-product-atc]') ||
      document.querySelector('form[action*="/cart/add"] [data-product-atc]');
    if (btn && btn.closest('form')) return btn.closest('form');
    return document.querySelector('[data-product-form]') || document.querySelector('form[action*="/cart/add"]');
  }

  function buildMainItem(form) {
    var idEl = form.querySelector('[name="id"]');
    if (!idEl || !idEl.value) return null;
    var qtyEl = form.querySelector('[name="quantity"]');
    var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
    var item = { id: idEl.value, quantity: qty, properties: {} };

    // Preserve any line-item properties / selling plan the theme set on the form.
    form.querySelectorAll('[name^="properties["]').forEach(function (el) {
      var m = el.name.match(/^properties\[(.+)\]$/);
      if (!m) return;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      item.properties[m[1]] = el.value;
    });
    var sp = form.querySelector('[name="selling_plan"]');
    if (sp && sp.value) item.selling_plan = sp.value;

    if (!Object.keys(item.properties).length) delete item.properties;
    return { item: item, qty: qty };
  }

  function setButtonLoading(form, on) {
    var btn = form.querySelector('[data-product-atc]');
    if (!btn) return;
    if (on) {
      btn.classList.add('disabled');
      btn.setAttribute('aria-busy', 'true');
    } else {
      btn.classList.remove('disabled');
      btn.removeAttribute('aria-busy');
    }
  }

  function ensureMessageBox(widget) {
    var box = widget.querySelector('.assembly-addon__message');
    if (!box) {
      box = document.createElement('div');
      box.className = 'assembly-addon__message';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      widget.appendChild(box);
    }
    return box;
  }

  function showMessage(widget, html, state) {
    var box = ensureMessageBox(widget);
    box.setAttribute('data-state', state);
    box.innerHTML = html;
    box.hidden = false;
  }

  function clearMessage(widget) {
    var box = widget.querySelector('.assembly-addon__message');
    if (box) box.hidden = true;
  }

  function submitWithAssembly(form, widget, assemblyVariantId) {
    var main = buildMainItem(form);
    if (!main) { form.submit(); return; } // No variant resolved — fall back to native submit.

    var redirect = widget.getAttribute('data-cart-redirect') === 'true';
    var assemblyItem = {
      id: assemblyVariantId,
      quantity: main.qty,
      properties: {}
    };
    assemblyItem.properties[ASSEMBLY_FLAG] = 'true';
    assemblyItem.properties[ASSEMBLY_PARENT] = widget.getAttribute('data-assembly-parent-id');
    assemblyItem.properties._assembly_for = widget.getAttribute('data-assembly-parent-title') || '';

    form.__assemblyBusy = true;
    setButtonLoading(form, true);
    clearMessage(widget);

    fetch(ADD_URL + '.js', {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify({ items: [main.item, assemblyItem] })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) throw res.data;
        return getCart();
      })
      .then(function (cart) {
        notifyCart(cart);
        document.dispatchEvent(new Event('closeFlyouts'));
        if (redirect || document.body.classList.contains('template-cart')) {
          window.location.href = CART_URL;
          return;
        }
        showMessage(
          widget,
          '✓ Added to cart with Professional Assembly. <a href="' + CART_URL + '">View cart</a>',
          'success'
        );
        setButtonLoading(form, false);
        form.__assemblyBusy = false;
      })
      .catch(function (err) {
        setButtonLoading(form, false);
        form.__assemblyBusy = false;
        var msg = (err && (err.description || err.message)) || 'Could not add to cart. Please try again.';
        showMessage(widget, msg, 'error');
      });
  }

  function initWidget(widget) {
    if (widget.__assemblyInit) return;
    widget.__assemblyInit = true;

    var checkbox = widget.querySelector('[data-assembly-checkbox]');
    var priceEl = widget.querySelector('[data-assembly-price]');
    var mapEl = widget.querySelector('[data-assembly-map]');
    if (!checkbox || !mapEl) return;

    var map;
    try { map = JSON.parse(mapEl.textContent || '{}'); } catch (e) { map = {}; }

    var form = findProductForm();
    if (!form) return;

    function currentVariantId() {
      var el = form.querySelector('[name="id"]');
      return el ? String(el.value) : '';
    }

    // Show/price the checkbox for the selected variant; hide it for variants
    // that have no assembly option.
    function syncTo(variantId) {
      var entry = map[String(variantId)];
      if (entry) {
        widget.hidden = false;
        widget.setAttribute('data-assembly-variant-id', entry.vid);
        if (priceEl) priceEl.textContent = '+' + entry.label;
      } else {
        widget.hidden = true;
        widget.removeAttribute('data-assembly-variant-id');
        checkbox.checked = false;
        clearMessage(widget);
      }
    }

    syncTo(currentVariantId());

    // Empire's <variant-selection> fires a non-bubbling 'variant-change' event.
    var vs = document.querySelector('variant-selection');
    if (vs) {
      vs.addEventListener('variant-change', function (e) {
        var v = e.detail && e.detail.variant;
        syncTo(v ? v.id : currentVariantId());
      });
    }
    // Fallback: re-read the variant id after any option change in the form.
    form.addEventListener('change', function () { setTimeout(function () { syncTo(currentVariantId()); }, 0); });

    // Capture phase runs before the theme's bubble-phase jQuery submit handler,
    // so stopImmediatePropagation cleanly preempts it only when assembly is ticked.
    form.addEventListener('submit', function (event) {
      if (!checkbox.checked || widget.hidden) return;   // not selected/applicable → theme handles it
      if (form.__assemblyBusy) return;                  // in-flight guard
      var assemblyVariantId = widget.getAttribute('data-assembly-variant-id');
      if (!assemblyVariantId) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      submitWithAssembly(form, widget, assemblyVariantId);
    }, true);

    checkbox.addEventListener('change', function () { clearMessage(widget); });
  }

  /* ------------------------------------------------------------------ *
   * 2) Cart validation — drop orphaned assembly line items
   * ------------------------------------------------------------------ */

  function validateCart() {
    if (window.__assemblyValidating) return;
    window.__assemblyValidating = true;

    getCart()
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) { window.__assemblyValidating = false; return; }

        var parentPresent = {};
        cart.items.forEach(function (it) {
          var p = it.properties || {};
          if (p[ASSEMBLY_FLAG] === 'true') return;       // assembly lines are not parents
          parentPresent[String(it.product_id)] = true;
        });

        var orphanKeys = [];
        cart.items.forEach(function (it) {
          var p = it.properties || {};
          if (p[ASSEMBLY_FLAG] !== 'true') return;
          var parentId = String(p[ASSEMBLY_PARENT] || '');
          if (!parentPresent[parentId]) orphanKeys.push(it.key);
        });

        if (!orphanKeys.length) { window.__assemblyValidating = false; return; }

        var updates = {};
        orphanKeys.forEach(function (k) { updates[k] = 0; });

        return fetch(UPDATE_URL + '.js', {
          method: 'POST',
          headers: jsonHeaders(),
          credentials: 'same-origin',
          body: JSON.stringify({ updates: updates })
        })
          .then(function (r) { return r.json(); })
          .then(function (cart2) {
            window.__assemblyValidating = false;
            notifyCart(cart2);
            if (document.body.classList.contains('template-cart')) {
              window.location.reload(); // refresh the cart page DOM to drop the removed row
            }
          });
      })
      .catch(function () { window.__assemblyValidating = false; });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('.assembly-addon').forEach(initWidget);
    validateCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run validation whenever the cart changes (drawer/flyout removals, etc.).
  window.addEventListener('cartcount:update', function () { validateCart(); });

  // Support theme-editor section reloads on the product page.
  document.addEventListener('shopify:section:load', function (e) {
    var widgets = (e.target || document).querySelectorAll ? (e.target || document).querySelectorAll('.assembly-addon') : [];
    widgets.forEach(initWidget);
  });
})();
